/**
 * Team presence (ทีมออนไลน์, docs/api/ORGANIZE.md): one heartbeat per tab about every 30 s while
 * the tab is visible (and at once when the open design changes), a "leave" when the tab closes.
 * The answer (who is online, which design they have open, who holds which edit lease) is shared
 * by every screen through `usePresence()`; nothing here talks to any other service.
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import type { Presence } from '../api/types.js';
import { newId } from '../lib/ids.js';
import { useSession } from './session.js';

export const HEARTBEAT_MS = 30_000;
const TAB_KEY = 'xc.presence.tab';

function tabId(): string {
  try {
    const v = sessionStorage.getItem(TAB_KEY);
    if (v) return v;
    const id = newId();
    sessionStorage.setItem(TAB_KEY, id);
    return id;
  } catch {
    return newId();
  }
}

class PresenceService {
  private workspaceId: string | null = null;
  private designId: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly clientId = tabId();
  private listeners = new Set<() => void>();
  private bound = false;
  data: Presence | null = null;

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit() {
    for (const fn of this.listeners) fn();
  }

  start(workspaceId: string) {
    if (this.workspaceId === workspaceId) return;
    this.stop();
    this.workspaceId = workspaceId;
    this.bind();
    void this.beat();
  }

  stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.workspaceId) void api.leavePresence(this.workspaceId, this.clientId).catch(() => undefined);
    this.workspaceId = null;
    this.data = null;
    this.emit();
  }

  setDesign(id: string | null) {
    if (this.designId === id) return;
    this.designId = id;
    if (this.workspaceId) void this.beat();
  }

  private bind() {
    if (this.bound || typeof document === 'undefined') return;
    this.bound = true;
    document.addEventListener('visibilitychange', () => {
      if (!this.workspaceId) return;
      if (document.visibilityState === 'visible') void this.beat();
      else if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    });
    window.addEventListener('pagehide', () => {
      if (this.workspaceId) void api.leavePresence(this.workspaceId, this.clientId).catch(() => undefined);
    });
  }

  private async beat() {
    const w = this.workspaceId;
    if (!w) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    try {
      const data = await api.heartbeat(w, { clientId: this.clientId, designId: this.designId });
      if (this.workspaceId !== w) return;
      this.data = data;
      this.emit();
    } catch {
      /* offline or signed out: keep the last answer, try again on the next beat */
    }
    if (this.workspaceId === w && (typeof document === 'undefined' || document.visibilityState === 'visible')) {
      this.timer = setTimeout(() => void this.beat(), HEARTBEAT_MS);
    }
  }
}

export const presence = new PresenceService();

/** Mounted once by the app: heartbeats while signed in with a workspace. */
export function usePresenceHeartbeat() {
  const session = useSession();
  const w = session.status === 'authenticated' && !session.mustChangePassword ? session.workspace?.id ?? null : null;
  useEffect(() => {
    if (w) presence.start(w);
    else presence.stop();
  }, [w]);
}

/** The latest presence answer for the current workspace (null until the first heartbeat). */
export function usePresence(): Presence | null {
  const [, setTick] = useState(0);
  useEffect(() => presence.subscribe(() => setTick((t) => t + 1)), []);
  return presence.data;
}

/** Tell the team which design this tab has open (null when leaving the editor). */
export function useOpenDesign(designId: string | null) {
  useEffect(() => {
    presence.setDesign(designId);
    return () => presence.setDesign(null);
  }, [designId]);
}

/** designId → the other member who holds its edit lease (your own tabs are not listed). */
export function editingByOthers(p: Presence | null): Map<string, string> {
  const out = new Map<string, string>();
  for (const e of p?.editing ?? []) if (!e.self) out.set(e.designId, e.displayName);
  return out;
}

export const initialsOf = (name: string) => {
  const t = name.trim();
  if (!t) return '?';
  const parts = t.split(/[\s._-]+/).filter(Boolean);
  const first = [...(parts[0] ?? t)][0] ?? '?';
  const second = parts.length > 1 ? [...parts[1]!][0] ?? '' : '';
  return (first + second).toUpperCase();
};
